export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

/** All combinations of option values, e.g. [{Size: 'S', Colour: 'Sage'}, ...] */
export function cartesianOptions(options: { name: string; values: string[] }[]): Record<string, string>[] {
  return options.reduce<Record<string, string>[]>(
    (acc, opt) => acc.flatMap((combo) => opt.values.map((v) => ({ ...combo, [opt.name]: v }))),
    [{}],
  );
}

export function variantTitle(options: { name: string }[], values: Record<string, string>): string {
  if (options.length === 0) return 'Default';
  return options.map((o) => values[o.name]).filter(Boolean).join(' / ');
}

/**
 * Lays translated copy over the English original. Non-empty strings win; empty strings,
 * missing keys and keys the original does not have are ignored. Arrays merge by index.
 */
export function overlayTranslation<T>(base: T, tr: unknown): T {
  if (tr === null || tr === undefined) return base;
  if (typeof base === 'string') return (typeof tr === 'string' && tr.trim() ? tr : base) as T;
  if (Array.isArray(base)) return (Array.isArray(tr) ? base.map((b, i) => overlayTranslation(b, tr[i])) : base) as T;
  if (base && typeof base === 'object' && typeof tr === 'object' && !Array.isArray(tr)) {
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
    for (const [k, v] of Object.entries(tr as Record<string, unknown>)) {
      if (k in out) out[k] = overlayTranslation(out[k], v);
    }
    return out as T;
  }
  return base;
}

/** Colour names used for swatches in the storefront. */
export const SWATCH_COLORS: Record<string, string> = {
  chalk: '#ece6db',
  bone: '#e3d9c6',
  oat: '#d4bf9c',
  sand: '#d8c6a8',
  sage: '#a3ad92',
  celadon: '#b4c7ba',
  moss: '#6d7356',
  charcoal: '#3a3733',
  rust: '#a8532f',
  terracotta: '#b9744f',
  ink: '#2e3a4d',
  honey: '#c08a3e',
  blush: '#d9b3a2',
  white: '#f5f3ef',
  black: '#1e1d1b',
  natural: '#d9ccb5',
  cream: '#ece0c4',
  red: '#a8382c',
  green: '#4f6b4a',
  blue: '#3f5f7f',
  mustard: '#c49a3a',
  brown: '#6b4a32',
  orange: '#c46a2f',
  brass: '#b8964f',
  chrome: '#c9ccce',
};

export function swatchFor(value: string): string | null {
  const key = value.toLowerCase().split(/[\s/]+/).find((w) => SWATCH_COLORS[w]);
  return key ? SWATCH_COLORS[key] : null;
}

export const COLOR_OPTION_NAMES = ['colour', 'color', 'glaze', 'finish'];
export function isColorOption(name: string): boolean {
  return COLOR_OPTION_NAMES.includes(name.toLowerCase());
}
